import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Filter as FilterIcon, AlertTriangle } from 'lucide-react';
import PackagingForm from './PackagingForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill } from '../../../shared/Pill';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import brandLabel from '../../../shared/brandLabel';
import { PACKAGING_TYPES, packagingTypeLabel } from './packagingTypes';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { useShipmentDimensionUnit, useShipmentWeightUnit, shipmentDimensionLabel, formatDimensions } from '../../../shared/useShipmentWeightUnit';
import { formatNumber, formatMoney } from '../../../shared/numberFormat';
import { WEIGHT_TO_KG } from '../packageShared';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const FILTER_SCREEN_KEY = 'warehouse-packaging';
const DEFAULT_FILTERS = { status: 'active', packaging_type: '', brand_id: '', flat_rate: '', can_ship: '' };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: 'inherit', textAlign: 'left' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// "0.02 / 5" — Empty / Maximum Weight, a blank one as "—"; null when neither
// is entered. A flat-rate product's Maximum is its carrier's limit.
function formatWeights(row, weightUnit) {
  const max = row.flat_rate_max_kg != null ? row.flat_rate_max_kg / (WEIGHT_TO_KG[weightUnit] ?? 1) : row.max_weight;
  if (row.empty_weight == null && max == null) return null;
  const show = (v) => (v == null ? '—' : formatNumber(v, { maxDecimals: 3 }));
  return `${show(row.empty_weight)} / ${show(max)}`;
}

const dim = (v) => (v === null || v === undefined || v === '' ? '—' : formatNumber(v, { decimals: 1 }));

// A Satchel shows its Flat Size (length × width, reference only), a Mailer
// its Flat Size (kept as its inside length and width, issue #363);
// anything else its outside size.
function sizeLabel(row) {
  if (row.packaging_type === 'mailer') {
    if (row.inside_length == null && row.inside_width == null) return null;
    return `flat ${dim(row.inside_length)} × ${dim(row.inside_width)}`;
  }
  if (row.packaging_type !== 'satchel') return formatDimensions(row);
  if (row.length == null && row.width == null) return null;
  return `flat ${dim(row.length)} × ${dim(row.width)}`;
}

// The line under the size: a Box's inside size, a Mailer's Max Thickness.
function insideLabel(row) {
  if (row.inside_length == null) return null;
  if (row.packaging_type === 'mailer') return `max thickness ${dim(row.inside_height)}`;
  return `inside ${formatDimensions({ length: row.inside_length, width: row.inside_width, height: row.inside_height })}`;
}

// "Australia Post · Satchel Medium", or just the Carrier with no product picked.
function flatRateLabel(row) {
  return [row.carrier?.name, row.flat_rate_product_name].filter(Boolean).join(' · ');
}

// Warehouse › Packaging (issues #299, #313) — the boxes, satchels,
// mailers and envelopes a package can be packed in, any of them inside
// another or outermost, each with default dimensions that fill in when
// it's picked on Pick Order or at Ship. Same list shape as SourceTable.js,
// plus Dimensions, Empty / Maximum Weight, Carrier Flat-Rate, Consumable
// Product and its Cost (issue #314). Retired with Deactivate, never deleted.
// Type column and filter, Can Ship column (issue #317).
// Search, Filters drawer and SavedFilterTabs follow EmailTemplateTable.js.
function PackagingTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const navigate = useNavigate();
  const dismissError = useDismissError();
  const dimensionUnit = useShipmentDimensionUnit();
  const weightUnit = useShipmentWeightUnit();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  // Every Brand, deactivated ones too, so a saved Brand filter or a linked
  // Product's Brand still reads by name.
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { status, packaging_type: typeFilter, brand_id: brandFilter, flat_rate: flatRateFilter, can_ship: canShipFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Ad hoc "Filters" panel — draft is null while closed, so opening always
  // starts from the currently-applied filters (same convention as EmailTemplateTable).
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/brands', { params: { status: 'all' } })
      .then((res) => setBrands(res.data.data || []))
      .catch(() => setBrands([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, typeFilter, brandFilter, flatRateFilter, canShipFilter, search, sortBy, sortOrder, page, limit]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
    setPage(1);
  };
  const sortArrow = (col) => (sortBy === col ? (sortOrder === 'ASC' ? ' ▲' : ' ▼') : '');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/packagings', { params: { status, packaging_type: typeFilter || undefined, brand_id: brandFilter || undefined, flat_rate: flatRateFilter || undefined, can_ship: canShipFilter || undefined, search: search || undefined, sortBy, sortOrder, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Packaging "${item.name}"? Packages already packed in it keep it.`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/packagings/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/packagings/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/packagings/${editItem.id}`, data);
      } else {
        await axios.post('/api/packagings', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="active">Active only</option>
          <option value="inactive">Deactivated only</option>
          <option value="all">Active & Deactivated</option>
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Type</label><br />
        <select className="styled-input" value={draft.packaging_type || ''} onChange={(e) => setDraftField('packaging_type', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All types</option>
          {PACKAGING_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Brand</label><br />
        <select className="styled-input" value={draft.brand_id || ''} onChange={(e) => setDraftField('brand_id', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All brands</option>
          <option value="none">(no brand)</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{brandLabel(b)}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Flat-Rate</label><br />
        <select className="styled-input" value={draft.flat_rate || ''} onChange={(e) => setDraftField('flat_rate', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All</option>
          <option value="yes">Carrier flat-rate only</option>
          <option value="no">Own packaging only</option>
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Can Ship</label><br />
        <select className="styled-input" value={draft.can_ship || ''} onChange={(e) => setDraftField('can_ship', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All</option>
          <option value="yes">Can ship on its own</option>
          <option value="no">Inner boxes only</option>
        </select>
      </div>
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Packaging</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Packaging
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid ' + (searchFocused ? '#1976d2' : '#d0d5dd'), boxShadow: searchFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search packaging..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '14px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Packaging list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <PackagingForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            brands={brands}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '14px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '19%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('name')}>Name{sortArrow('name')}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('type')}>Type{sortArrow('type')}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }} title="Can ship on its own">Can Ship</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('brand')}>Brand{sortArrow('brand')}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>{shipmentDimensionLabel(dimensionUnit)}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} title="Empty / Maximum Weight">{weightUnit ? `Weight (${weightUnit})` : 'Weight'}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Carrier Flat-Rate</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Product</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>Cost</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {item.name}
                    {item.dimension_warnings?.length > 0 && (
                      <span
                        title={item.dimension_warnings.join('\n')}
                        aria-label={item.dimension_warnings.join('. ')}
                        style={{ display: 'flex', color: '#d97706', cursor: 'help' }}
                      >
                        <AlertTriangle size={16} />
                      </span>
                    )}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, whiteSpace: 'nowrap' }}>
                  {packagingTypeLabel(item.packaging_type)}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, textAlign: 'center' }}>{item.can_ship_alone === false ? '' : '✓'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.product?.brand ? brandLabel(item.product.brand) : undefined}>
                  {item.product?.brand ? item.product.brand.name : '—'}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, whiteSpace: 'nowrap' }}>
                  {sizeLabel(item) || '—'}
                  {insideLabel(item) && (
                    <div style={{ fontSize: 12, color: '#8a94a6' }}>{insideLabel(item)}</div>
                  )}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, whiteSpace: 'nowrap' }}>{formatWeights(item, weightUnit) || '—'}</td>
                <td style={{ padding: '6px 8px', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={flatRateLabel(item) || undefined}>
                  {flatRateLabel(item) || '—'}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} >
                  {item.product ? (
                    <button type="button" style={LINK_BUTTON} title={item.product.name} onClick={(e) => { e.stopPropagation(); navigate(`/products?edit=${item.product.id}`); }}>
                      {item.product.sku}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right' }}>{item.packaging_cost != null ? formatMoney(item.packaging_cost) : '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Packaging"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default PackagingTable;
